/**
 * Client-safe portal payment summary and statement of account.
 * Pure — the Cloud Function copy lives at functions/src/portalStatement.ts.
 */

import {
  activeMilestoneInvoices,
  formatInvoiceDollars,
  invoiceClosesMilestone,
  invoiceIsVoid,
  type MilestoneInvoiceRef,
} from './partialInvoiceAllocation';

export interface StatementPaymentInput {
  id: string;
  amount: number;
  /** ISO date. */
  date: string;
  quickbooksInvoiceId?: string;
  label?: string;
}

export interface PortalMilestoneSummary {
  installmentId: string;
  label: string;
  amount: number;
  status: 'not_billed' | 'open' | 'partial' | 'paid';
  amountBilled: number;
  amountPaid: number;
  amountUnbilled: number;
  partiallyBilled: boolean;
  /** Structure groups on the open invoices for this milestone. */
  structureGroupLabels: string[];
  /** ISO time once the milestone is fully invoiced. */
  invoicedAt?: string;
  paidAt?: string;
}

export interface StatementLine {
  id: string;
  date: string;
  kind: 'invoice' | 'payment' | 'design_fee';
  label: string;
  detail?: string;
  structureGroupLabels?: string[];
  charge?: number;
  payment?: number;
  balanceAfter: number;
  quickbooksInvoiceNum?: string;
}

export function invoiceCollectedAmount(invoice: MilestoneInvoiceRef): number {
  const amount = Math.round(Number(invoice.amount) || 0);
  if (amount <= 0 || invoiceIsVoid(invoice.status)) return 0;
  if (invoice.status === 'paid') return amount;
  const balance = Number(invoice.balance);
  if (!Number.isFinite(balance)) return 0;
  return Math.max(0, Math.min(amount, amount - Math.round(balance)));
}

/**
 * Shared installment money figures for the portal card, staff Client preview,
 * statement, and partial-payment requests.
 *
 * `amount` must be the live schedule installment total (base + every signed
 * change order folded into that installment). Never prefer a mirrored
 * amountUnbilled snapshot — later COs can raise the schedule without rewriting
 * the portal mirror.
 */
export function installmentBillingFigures(args: {
  amount: number;
  amountBilled?: number | null;
  amountPaid?: number | null;
}): {
  amount: number;
  amountBilled: number;
  amountPaid: number;
  /** Remaining to bill / not yet billed. */
  amountUnbilled: number;
  dueNow: number;
  remaining: number;
} {
  const amount = Math.max(0, Math.round(Number(args.amount) || 0));
  const amountBilled = Math.max(0, Math.round(Number(args.amountBilled) || 0));
  const amountPaid = Math.max(0, Math.round(Number(args.amountPaid) || 0));
  return {
    amount,
    amountBilled,
    amountPaid,
    amountUnbilled: Math.max(0, amount - amountBilled),
    dueNow: Math.max(0, amountBilled - amountPaid),
    remaining: Math.max(0, amount - amountPaid),
  };
}

function iso(value: unknown): string {
  if (!value) return '';
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? '' : new Date(parsed).toISOString();
  }
  if (typeof value === 'number' && Number.isFinite(value)) return new Date(value).toISOString();
  const maybe = value as { toDate?: () => Date; toMillis?: () => number };
  if (typeof maybe.toDate === 'function') {
    const date = maybe.toDate();
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toISOString() : '';
  }
  if (typeof maybe.toMillis === 'function') return new Date(maybe.toMillis()).toISOString();
  return '';
}

export function summarizeMilestone(args: {
  installmentId: string;
  label: string;
  amount: number;
  invoices: MilestoneInvoiceRef[];
}): PortalMilestoneSummary {
  const active = activeMilestoneInvoices(args.invoices, args.installmentId);
  const billedSum = active.reduce((sum, invoice) => sum + Math.round(Number(invoice.amount) || 0), 0);
  const paidSum = active.reduce((sum, invoice) => sum + invoiceCollectedAmount(invoice), 0);
  const figures = installmentBillingFigures({
    amount: args.amount,
    amountBilled: billedSum,
    amountPaid: paidSum,
  });
  const { amount, amountBilled, amountPaid, amountUnbilled } = figures;
  const closed = active.some((invoice) => invoiceClosesMilestone(invoice));
  const partiallyBilled = active.length > 0 && !closed && amountBilled < amount;
  const fullyBilled = closed || (amount > 0 && amountBilled >= amount);
  const fullyPaid = fullyBilled && amountPaid >= amountBilled && amountBilled > 0;
  let status: PortalMilestoneSummary['status'] = 'not_billed';
  if (active.length === 0) status = 'not_billed';
  else if (fullyPaid) status = 'paid';
  else if (amountPaid > 0) status = 'partial';
  else status = 'open';

  const paidInvoice = [...active].reverse().find((invoice) => invoice.status === 'paid' && invoice.paidAt);
  const closing = active.find((invoice) => invoiceClosesMilestone(invoice));
  return {
    installmentId: args.installmentId,
    label: args.label,
    amount,
    status,
    amountBilled,
    amountPaid,
    amountUnbilled,
    partiallyBilled,
    structureGroupLabels: Array.from(new Set(
      active.flatMap((invoice) => invoice.structureGroupLabels || []).map((label) => label.trim()).filter(Boolean),
    )),
    invoicedAt: fullyBilled ? iso(closing?.createdAt || active[active.length - 1]?.createdAt) || undefined : undefined,
    paidAt: fullyPaid ? iso(paidInvoice?.paidAt) || undefined : undefined,
  };
}

function invoiceLabel(invoice: MilestoneInvoiceRef, milestoneLabel: string): string {
  const groups = (invoice.structureGroupLabels || []).filter(Boolean);
  if (groups.length) return `${milestoneLabel} — ${groups.join(', ')}`;
  return milestoneLabel;
}

export function buildPortalStatement(args: {
  milestones: Array<{ id: string; label: string; amount: number }>;
  invoices: MilestoneInvoiceRef[];
  payments: StatementPaymentInput[];
  designFee?: { amount: number; date?: string } | null;
}): StatementLine[] {
  const milestoneLabel = new Map(args.milestones.map((row) => [row.id, row.label]));
  type Draft = StatementLine & { sort: number };
  const drafts: Draft[] = [];

  if (args.designFee && Math.round(args.designFee.amount) > 0) {
    drafts.push({
      id: 'design-fee',
      date: args.designFee.date || '',
      kind: 'design_fee',
      label: 'Design fee',
      detail: 'Paid separately from the contract',
      payment: Math.round(args.designFee.amount),
      balanceAfter: 0,
      sort: args.designFee.date ? Date.parse(args.designFee.date) || 0 : 0,
    });
  }

  const active = (args.invoices || []).filter((invoice) => invoice.installmentId && !invoiceIsVoid(invoice.status));
  const paymentsByInvoice = new Map<string, StatementPaymentInput[]>();
  for (const payment of args.payments) {
    const invoiceId = (payment.quickbooksInvoiceId || '').trim();
    if (!invoiceId || Math.round(payment.amount) === 0) continue;
    const list = paymentsByInvoice.get(invoiceId) || [];
    list.push(payment);
    paymentsByInvoice.set(invoiceId, list);
  }

  active.forEach((invoice, index) => {
    const invoiceId = invoice.quickbooksInvoiceId || `invoice-${index}`;
    const when = iso(invoice.createdAt) || iso(invoice.paidAt);
    const label = invoiceLabel(invoice, milestoneLabel.get(invoice.installmentId || '') || 'Invoice');
    const groups = (invoice.structureGroupLabels || []).filter(Boolean);
    drafts.push({
      id: `inv-${invoiceId}`,
      date: when,
      kind: 'invoice',
      label,
      detail: groups.length ? groups.join(', ') : 'Whole contract',
      structureGroupLabels: groups.length ? groups : undefined,
      charge: Math.round(Number(invoice.amount) || 0),
      balanceAfter: 0,
      quickbooksInvoiceNum: invoice.quickbooksInvoiceNum,
      sort: (when ? Date.parse(when) : 0) + index,
    });

    const linked = paymentsByInvoice.get(invoice.quickbooksInvoiceId || '') || [];
    let linkedSum = 0;
    linked.forEach((payment, paymentIndex) => {
      const amount = Math.round(payment.amount);
      if (amount === 0) return;
      linkedSum += amount;
      drafts.push({
        id: `pay-${payment.id}`,
        date: payment.date,
        kind: 'payment',
        label: payment.label || 'Payment received',
        detail: invoice.quickbooksInvoiceNum ? `Applied to invoice #${invoice.quickbooksInvoiceNum}` : label,
        structureGroupLabels: groups.length ? groups : undefined,
        payment: amount,
        balanceAfter: 0,
        quickbooksInvoiceNum: invoice.quickbooksInvoiceNum,
        sort: (payment.date ? Date.parse(payment.date) : 0) + 0.5 + paymentIndex / 100,
      });
    });
    const collected = invoiceCollectedAmount(invoice);
    const residual = collected - linkedSum;
    if (residual > 0) {
      const paidWhen = iso(invoice.paidAt) || when;
      drafts.push({
        id: `pay-residual-${invoiceId}`,
        date: paidWhen,
        kind: 'payment',
        label: 'Payment received',
        detail: invoice.quickbooksInvoiceNum
          ? `Applied to invoice #${invoice.quickbooksInvoiceNum}`
          : label,
        structureGroupLabels: groups.length ? groups : undefined,
        payment: residual,
        balanceAfter: 0,
        quickbooksInvoiceNum: invoice.quickbooksInvoiceNum,
        sort: (paidWhen ? Date.parse(paidWhen) : 0) + 0.6,
      });
    }
  });

  drafts.sort((a, b) => a.sort - b.sort || a.kind.localeCompare(b.kind));
  let balance = 0;
  return drafts.map((draft) => {
    if (draft.kind !== 'design_fee') {
      balance += Math.round(draft.charge || 0) - Math.round(draft.payment || 0);
    }
    const { sort: _sort, structureGroupLabels, ...line } = draft;
    return {
      ...line,
      ...(structureGroupLabels?.length ? { structureGroupLabels } : {}),
      balanceAfter: balance,
    };
  });
}

export function milestoneCoverageDetail(summary: Pick<
  PortalMilestoneSummary,
  'amount' | 'amountBilled' | 'amountPaid' | 'amountUnbilled' | 'partiallyBilled'
>): string {
  return `Invoiced ${formatInvoiceDollars(summary.amountBilled)} of ${formatInvoiceDollars(summary.amount)} · Paid ${formatInvoiceDollars(summary.amountPaid)} · Not yet billed ${formatInvoiceDollars(summary.amountUnbilled)}`;
}

export interface StatementScheduleStatus {
  status?: string;
  amountBilled?: number;
  amountPaid?: number;
  partiallyBilled?: boolean;
  structureGroupLabels?: string[];
  /** When this installment's invoice was created. */
  invoicedAt?: string | Date;
  /** When this installment was paid. */
  paidAt?: string | Date;
}

export interface StatementScheduleFold {
  reference: string;
  title: string;
  amount: number;
}

export interface StatementScheduleRow {
  id: string;
  label: string;
  detail: string;
  amount: number;
  invoiced: number;
  paid: number;
  remaining: number;
  statusLabel: string;
  /** Structure groups included on the invoices for this row. */
  structureGroupLabels?: string[];
  /** ISO time the installment invoice was created. Blank while only part of it is billed. */
  invoicedAt?: string;
  /** ISO time the installment was paid. */
  paidAt?: string;
  /** Installment rows can carry folded change orders. Immediate change orders are their own rows. */
  kind?: 'installment' | 'change_order';
  baseAmount?: number;
  folded?: StatementScheduleFold[];
}

export interface StatementChangeOrderInput {
  id?: string;
  number?: number;
  title?: string;
  deltaAmount?: number;
  status?: string;
  billingMode?: 'immediate' | 'contract_terms' | string;
}

export interface StatementChangeOrderLine {
  id: string;
  numberLabel: string;
  title: string;
  amount: number;
  /** Contract terms, or billed upon approval. */
  terms: string;
}

/**
 * Signed change orders only. Unsigned orders are not part of the contract yet.
 * A credit always uses contract terms.
 */
export function statementChangeOrderLines(
  changeOrders: StatementChangeOrderInput[] | null | undefined,
): StatementChangeOrderLine[] {
  return (changeOrders || [])
    .filter((co) => co.status === 'approved')
    .slice()
    .sort((a, b) => (Number(a.number) || 0) - (Number(b.number) || 0))
    .map((co) => {
      const amount = Math.round(Number(co.deltaAmount) || 0);
      const n = Math.max(1, Math.floor(Number(co.number) || 1));
      const terms = amount < 0 || co.billingMode === 'contract_terms'
        ? 'Contract terms'
        : 'Billed upon approval';
      return {
        id: co.id || `co-${n}`,
        numberLabel: `CO-${String(n).padStart(3, '0')}`,
        title: (co.title || '').trim(),
        amount,
        terms,
      };
    });
}

function scheduleIsUnbilled(status: StatementScheduleStatus | undefined): boolean {
  const name = status?.status;
  return !name || name === 'not_billed' || name === 'voided' || name === 'removed_in_qb';
}

function scheduleStatusLabel(status: StatementScheduleStatus | undefined, amount: number): string {
  const billed = status?.amountBilled != null ? Math.round(status.amountBilled) : null;
  const paid = Math.round(status?.amountPaid ?? 0);
  const partly = !!status?.partiallyBilled || (billed != null && billed > 0 && billed < amount);
  if (scheduleIsUnbilled(status) && !(billed != null && billed > 0)) return 'Not yet invoiced';
  if (status?.status === 'paid' || (amount > 0 && paid >= amount && (billed ?? amount) >= amount)) {
    return 'Paid';
  }
  if (partly && paid <= 0) return 'Partial';
  if (status?.status === 'partial' || paid > 0) return 'Partially paid';
  if (status?.status === 'open' || (billed ?? 0) > 0) return 'Invoiced';
  return 'Not yet invoiced';
}

/**
 * The contract schedule is the statement from the day the project is signed.
 * Invoices and payments fill in the invoiced and paid columns; they are not
 * required for the statement to exist.
 */
export function statementScheduleRows(args: {
  rows: Array<{
    id: string;
    label: string;
    detail: string;
    amount: number;
    kind?: 'installment' | 'change_order';
    baseAmount?: number;
    folded?: StatementScheduleFold[];
  }>;
  statuses?: Record<string, StatementScheduleStatus | undefined>;
}): StatementScheduleRow[] {
  return args.rows.map((row) => {
    const status = args.statuses?.[row.id];
    const amount = Math.round(row.amount);
    const invoicedRaw = status?.amountBilled != null
      ? Math.round(status.amountBilled)
      : (status && !scheduleIsUnbilled(status) ? amount : 0);
    const paidRaw = status?.amountPaid != null
      ? Math.round(status.amountPaid)
      : (status?.status === 'paid' ? amount : 0);
    const figures = installmentBillingFigures({
      amount,
      amountBilled: invoicedRaw,
      amountPaid: paidRaw,
    });
    const structureGroupLabels = (status?.structureGroupLabels || []).map((label) => label.trim()).filter(Boolean);
    const folded = (row.folded || [])
      .filter((line) => line.amount !== 0)
      .map((line) => ({
        reference: line.reference,
        title: (line.title || '').trim(),
        amount: Math.round(line.amount),
      }));
    const invoicedAt = iso(status?.invoicedAt) || undefined;
    const paidAt = iso(status?.paidAt) || undefined;
    return {
      id: row.id,
      label: row.label,
      detail: row.detail,
      amount: figures.amount,
      invoiced: figures.amountBilled,
      paid: figures.amountPaid,
      remaining: figures.remaining,
      statusLabel: scheduleStatusLabel(status, figures.amount),
      structureGroupLabels: structureGroupLabels.length ? structureGroupLabels : undefined,
      ...(invoicedAt ? { invoicedAt } : {}),
      ...(paidAt ? { paidAt } : {}),
      kind: row.kind,
      baseAmount: row.baseAmount != null ? Math.round(row.baseAmount) : undefined,
      folded,
    };
  });
}

type ScheduleActivityLine = {
  kind?: string;
  label?: string;
  date?: string;
  structureGroupLabels?: string[];
  charge?: number;
  payment?: number;
  quickbooksInvoiceNum?: string;
};

function latestIso(dates: Array<string | undefined>): string | undefined {
  const valid = dates.filter((date): date is string => !!date && !Number.isNaN(Date.parse(date)));
  if (!valid.length) return undefined;
  valid.sort((a, b) => Date.parse(a) - Date.parse(b));
  return valid[valid.length - 1];
}

function activityMatchesInstallment(lineLabel: string, rowLabel: string): boolean {
  const label = lineLabel.trim();
  const row = rowLabel.trim();
  if (!label || !row) return false;
  if (label === row || label.startsWith(`${row} —`)) return true;
  return label.startsWith(row) && label.includes(' — ');
}

function paidDateForInvoice(
  activity: ScheduleActivityLine[] | undefined,
  invoiceNum: string | undefined,
  groups: string[],
): string | undefined {
  const wanted = groups.join(', ');
  const payments = (activity || []).filter((line) => {
    if (line.kind !== 'payment' || !line.date) return false;
    if (invoiceNum) return line.quickbooksInvoiceNum === invoiceNum;
    const labels = (line.structureGroupLabels || []).map((label) => label.trim()).filter(Boolean);
    return !!wanted && labels.join(', ') === wanted;
  });
  return latestIso(payments.map((line) => line.date));
}

/**
 * A partial invoice belongs on the structure-group line it billed, not in the
 * installment's Invoiced column. A whole-milestone invoice stays on the installment.
 */
export function scheduleBilledGroupLines(
  row: Pick<StatementScheduleRow, 'label' | 'amount' | 'invoiced' | 'structureGroupLabels'>,
  activity?: ScheduleActivityLine[],
): Array<{ label: string; invoiced: number; invoicedAt?: string; paidAt?: string }> {
  const invoiced = Math.round(row.invoiced);
  const amount = Math.round(row.amount);
  if (invoiced <= 0 || invoiced >= amount) return [];
  const fromActivity = (activity || [])
    .filter((line) => line.kind === 'invoice' && Math.round(line.charge || 0) > 0)
    .filter((line) => activityMatchesInstallment(line.label || '', row.label))
    .map((line) => {
      const groups = (line.structureGroupLabels || []).map((label) => label.trim()).filter(Boolean);
      const invoicedAt = line.date || undefined;
      const paidAt = paidDateForInvoice(activity, line.quickbooksInvoiceNum, groups);
      return {
        label: groups.join(', '),
        invoiced: Math.round(line.charge || 0),
        ...(invoicedAt ? { invoicedAt } : {}),
        ...(paidAt ? { paidAt } : {}),
      };
    })
    .filter((line) => line.label && line.invoiced > 0);
  if (fromActivity.length) return fromActivity;
  const labels = (row.structureGroupLabels || []).map((label) => label.trim()).filter(Boolean);
  if (!labels.length) return [];
  return [{ label: labels.join(', '), invoiced }];
}

/**
 * Dates for a whole-milestone invoice. A partial invoice keeps its dates on the
 * structure-group line instead, so this stays blank while the milestone is open.
 */
export function scheduleMilestoneDates(
  row: Pick<StatementScheduleRow, 'label' | 'invoicedAt' | 'paidAt'>,
  activity?: ScheduleActivityLine[],
): { invoicedAt?: string; paidAt?: string } {
  const invoice = (activity || []).find((line) => {
    if (line.kind !== 'invoice' || !line.date) return false;
    const label = (line.label || '').trim();
    const rowLabel = row.label.trim();
    return label === rowLabel || (label.startsWith(rowLabel) && !label.includes(' — '));
  });
  const invoicedAt = row.invoicedAt || invoice?.date || undefined;
  const paidAt = row.paidAt || paidDateForInvoice(activity, invoice?.quickbooksInvoiceNum, []) || undefined;
  return {
    ...(invoicedAt ? { invoicedAt } : {}),
    ...(paidAt ? { paidAt } : {}),
  };
}
